"use client";
import React, { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import Image from "next/image";
import { fontSizes } from "@/constants/constants";

const TripleColumn = ({ title, description, steps = [], image }) => {
  useEffect(() => {
    AOS.init({ duration: 800, offset: 100, once: true, easing: "ease-in-out" });
  }, []);

  return (
    <section className="relative overflow-hidden bg-ink bg-paper-grain py-20 px-4 md:px-8">
      <div aria-hidden className="pointer-events-none absolute -left-24 top-10 h-72 w-72 rounded-full bg-brand-500/20 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -right-20 bottom-0 h-72 w-72 rounded-full bg-gold/10 blur-3xl" />

      <div className="relative mx-auto max-w-6xl">
        {/* Header + featured book */}
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-7" data-aos="fade-right">
            <span className="eyebrow flex items-center justify-center gap-3 text-gold-soft lg:justify-start">
              <span className="h-px w-8 bg-gold/70" />
              How It Works
            </span>
            <h2 className={`mt-5 text-center font-display font-semibold text-white lg:text-left ${fontSizes.text_5_xl}`}>
              {title}
            </h2>
            {description && (
              <p className="mt-5 text-center leading-relaxed text-white/70 lg:text-left">
                {description}
              </p>
            )}
          </div>

          <div className="flex justify-center lg:col-span-5" data-aos="fade-left">
            <div className="relative">
              <div className="absolute -inset-3 rounded-[1.8rem] bg-gradient-to-br from-brand-400/30 to-gold/20 blur-lg" />
              <div className="relative overflow-hidden rounded-[1.6rem] bg-white/5 p-3 ring-1 ring-white/15 backdrop-blur">
                <Image src={image} alt="book" className="w-64 animate-float-slow" />
              </div>
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="relative mt-16">
          {/* connecting line — desktop */}
          <div className="absolute left-0 right-0 top-6 hidden h-px bg-gradient-to-r from-transparent via-gold/50 to-transparent lg:block" />

          <div className="grid grid-cols-1 gap-y-10 sm:grid-cols-2 lg:grid-cols-5 lg:gap-x-6">
            {steps.map((step, i) => (
              <div
                key={step.id}
                data-aos="fade-up"
                data-aos-delay={i * 100}
                className="group relative flex flex-col items-center text-center lg:items-start lg:text-left"
              >
                {/* node */}
                <span className="relative z-10 flex h-12 w-12 items-center justify-center rounded-full bg-gold font-display text-lg font-semibold text-ink shadow-gold-glow transition-transform duration-300 group-hover:scale-110">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="mt-5 font-display text-xl font-semibold text-white">
                  {step.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-white/65">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TripleColumn;
